import { COLOR_FILTERS_STATUS } from '@ipfabric/utils/api/Reports';
import {
  type ReportSeverityKey,
  reportSeverities,
} from '@ipfabric/utils/report';
import type { FC } from 'react';
import { Link } from '../Link/Link';
import { TextOverflowTooltip } from '../Tooltip/TextOverflowTooltip';
import { Tooltip } from '../Tooltip/Tooltip';
import { IntentResultBadge } from './IntentResultBadge';
import {
  intentResultBadgeVariants,
  intentVerificationTableVariants,
} from './IntentVerification.styles';
import type { IntentVerificationTableProps } from './IntentVerification.types';

const styles = intentVerificationTableVariants();

const GRID_TEMPLATE_COLUMNS =
  'minmax(auto, 215px) minmax(auto, 181px) 1fr auto';

export const IntentVerificationTable: FC<IntentVerificationTableProps> = (
  props,
) => {
  const handleSeverityClick = (
    column: string,
    severity: ReportSeverityKey | null,
  ) => {
    props.onSeverityClick?.(column, severity);
  };

  const customRowIcon = (
    <span className='ml-2'>
      <Tooltip content='User defined rule'>
        <i className='fa-fw fa-regular fa-circle-user text-sm' />
      </Tooltip>
    </span>
  );

  const renderRow = (row: (typeof props.data)[number]) => (
    <div className={styles.row()} key={row.id}>
      <div className={`${styles.cellWrapper()} ${styles.nameColumn()}`}>
        {row.descriptions?.general ? (
          <div className={styles.cellName()}>
            <Tooltip content={row.descriptions?.general}>
              <span>{row.name}</span>
            </Tooltip>
            {row.custom && customRowIcon}
          </div>
        ) : (
          <TextOverflowTooltip>
            <span className={styles.cellName()}>
              {row.name}
              {row.custom && customRowIcon}
            </span>
          </TextOverflowTooltip>
        )}
      </div>
      <div className={`${styles.cellWrapper()} ${styles.columnColumn()}`}>
        <TextOverflowTooltip>
          <span className={styles.cellColumn()}>{row.columnTitle}</span>
        </TextOverflowTooltip>
      </div>
      <div className={`${styles.cellWrapper()} ${styles.resultsColumn()}`}>
        <div className='flex items-center gap-3'>
          {reportSeverities.map((severity) => {
            const count = row.result?.checks?.[severity];
            return count !== undefined ? (
              <Tooltip
                key={severity}
                content={row.descriptions?.checks?.[severity]}
              >
                <IntentResultBadge
                  severity={severity}
                  count={count}
                  status={row.status}
                  onSeverityClick={(s: ReportSeverityKey | null) => {
                    handleSeverityClick(row.column, s);
                  }}
                  selected={
                    props.intentFilters?.[row.column] === severity.toString()
                  }
                />
              </Tooltip>
            ) : (
              <div key={severity} className={intentResultBadgeVariants()} />
            );
          })}
        </div>
      </div>
      <div className={`${styles.cellWrapper()} ${styles.actionsColumn()}`}>
        <div className={styles.actionsWrapper()}>
          <Link
            icon='fa-pen'
            iconPosition='start'
            size='small'
            renderAs='button'
            onClick={() => {
              props.onEditClick?.(row);
            }}
            disabled={row.status === COLOR_FILTERS_STATUS.run}
          >
            Edit
          </Link>
          <Link
            icon='fa-trash-can'
            iconPosition='start'
            size='small'
            renderAs='button'
            onClick={() => {
              props.onDeleteClick?.(row);
            }}
          >
            Delete
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={styles.root()}
      style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}
    >
      <div className={styles.row()}>
        <div className={styles.headerLabel()}>Name</div>
        <div className={styles.headerLabel()}>Column</div>
        <div className={styles.headerLabel()}>Results</div>
        <div className={styles.headerLabel()}>Actions</div>
      </div>
      {props.data.map((row) => renderRow(row))}
    </div>
  );
};
